import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, MoreVertical, UserPlus, ExternalLink, ArrowRightLeft, UserMinus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useDataLabel } from '@/lib/dataI18n';

/**
 * UAT Finding 10 — Organizational Workspace side sheet.
 *
 * Read-only member roster for a department. When `canManageMembers` is true
 * (admin scope) the row menu exposes Move / Remove and the footer surfaces an
 * "Assign existing employee" picker. All mutations go through the pre-existing
 * `PATCH /admin/users/:id` endpoint — NO new mutation endpoints introduced.
 *
 * Props:
 *   open, onOpenChange
 *   department          — dict with at least { id, name, code }
 *   apiPrefix           — 'client' | 'admin'
 *   companyId           — required for admin scope query params
 *   allDepartments      — used by the Move sub-menu
 *   canManageMembers    — true for admin embed, false for client portal
 *   onAfterChange()     — called after successful mutation so parent reloads
 */
export default function OrgDepartmentSheet({
  open, onOpenChange,
  department, apiPrefix = 'client',
  companyId, allDepartments = [],
  canManageMembers = false,
  onAfterChange,
}) {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const [loading, setLoading] = useState(false);
  const [busyMemberId, setBusyMemberId] = useState(null);
  const [payload, setPayload] = useState(null);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [assignOpen, setAssignOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [assignId, setAssignId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const buildMembersUrl = () => {
    if (apiPrefix === 'admin') {
      return `/admin/departments/${department.id}/members?company_id=${encodeURIComponent(companyId)}`;
    }
    return `/client/departments/${department.id}/members`;
  };

  const load = async () => {
    if (!department?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(buildMembersUrl());
      setPayload(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setPayload({ department, members: [], total: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) { setQuery(''); setSortKey('name'); load(); }
  }, [open, department?.id]);

  const filtered = useMemo(() => {
    const list = payload?.members || [];
    const q = query.trim().toLowerCase();
    let out = q
      ? list.filter((m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.position || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q))
      : list.slice();
    out.sort((a, b) => {
      if (sortKey === 'position') return (a.position || '').localeCompare(b.position || '');
      if (sortKey === 'joined') return String(b.joined_at || '').localeCompare(String(a.joined_at || ''));
      return (a.name || '').localeCompare(b.name || '');
    });
    return out;
  }, [payload, query, sortKey]);

  const mutateUser = async (userId, body, successKey) => {
    setBusyMemberId(userId);
    try {
      await api.patch(`/admin/users/${userId}`, body);
      toast.success(t(successKey, { defaultValue: 'Saved' }));
      await load();
      onAfterChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusyMemberId(null); }
  };

  const move = (userId, newDeptId) =>
    mutateUser(userId, { department_id: newDeptId }, 'pages.clientDepartments.org.movedToast');
  const remove = (userId) =>
    mutateUser(userId, { department_id: null }, 'pages.clientDepartments.org.removedToast');

  const openAssign = async () => {
    setAssignId('');
    setAssignOpen(true);
    try {
      const url = apiPrefix === 'admin'
        ? `/admin/employees/dept_less?company_id=${encodeURIComponent(companyId)}`
        : `/client/employees/dept_less`;
      const { data } = await api.get(url);
      setCandidates(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setCandidates([]);
    }
  };

  const confirmAssign = async () => {
    if (!assignId) return;
    setAssigning(true);
    try {
      await api.patch(`/admin/users/${assignId}`, { department_id: department.id });
      toast.success(t('pages.clientDepartments.org.assignedToast', { defaultValue: 'Employee assigned' }));
      setAssignOpen(false);
      setAssignId('');
      await load();
      onAfterChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setAssigning(false); }
  };

  const otherDepts = (allDepartments || []).filter((x) => x.id !== department?.id && !x.archived);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="org-dept-sheet"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {department ? tData(department) : ''}
            {payload?.total != null && (
              <Badge variant="secondary" className="text-xs tabular-nums">
                {t('pages.clientDepartments.org.members', { count: payload.total, defaultValue: '{{count}} members' })}
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {payload?.department?.manager
              ? <>{t('pages.clientDepartments.org.manager', { defaultValue: 'Manager' })}: {payload.department.manager.name}</>
              : t('pages.clientDepartments.org.noManager', { defaultValue: '— No manager assigned' })}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="h-3.5 w-3.5 absolute top-2.5 left-2.5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('pages.clientDepartments.org.searchPh', { defaultValue: 'Search name, position, email…' })}
                className="pl-8 h-9 text-sm"
                data-testid="org-sheet-search"
              />
            </div>
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger className="w-36 h-9 text-xs" data-testid="org-sheet-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">{t('pages.clientDepartments.org.sortName', { defaultValue: 'Sort: Name' })}</SelectItem>
                <SelectItem value="position">{t('pages.clientDepartments.org.sortPosition', { defaultValue: 'Sort: Position' })}</SelectItem>
                <SelectItem value="joined">{t('pages.clientDepartments.org.sortJoined', { defaultValue: 'Recently joined' })}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="py-16 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />{t('common.loading')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground italic" data-testid="org-sheet-empty">
              {query
                ? t('common.noResults', { defaultValue: 'No results' })
                : t('pages.clientDepartments.org.emptyMembers', { defaultValue: 'No one is in this department yet.' })}
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="org-sheet-list">
              {filtered.map((m) => (
                <li
                  key={m.id}
                  className="py-2.5 flex items-center gap-3"
                  data-testid={`org-sheet-row-${m.id}`}
                >
                  <div className="h-8 w-8 rounded-full bg-primary/15 text-xs font-semibold text-primary flex items-center justify-center shrink-0">
                    {m.avatar_initials || '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{m.position || '—'}{m.email ? ` · ${m.email}` : ''}</p>
                  </div>
                  {canManageMembers && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={busyMemberId === m.id} data-testid={`org-sheet-menu-${m.id}`}>
                          {busyMemberId === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <a href={`/admin/users?focus=${m.id}`} className="flex items-center gap-2">
                            <ExternalLink className="h-3.5 w-3.5" />
                            {t('pages.clientDepartments.org.openProfile', { defaultValue: 'Open profile' })}
                          </a>
                        </DropdownMenuItem>
                        {otherDepts.length > 0 && (
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                            className="p-0"
                          >
                            <Select onValueChange={(v) => move(m.id, v)}>
                              <SelectTrigger className="border-0 h-9 text-xs w-full justify-start gap-2 px-2" data-testid={`org-sheet-move-${m.id}`}>
                                <ArrowRightLeft className="h-3.5 w-3.5" />
                                <span>{t('pages.clientDepartments.org.moveTo', { defaultValue: 'Move to…' })}</span>
                              </SelectTrigger>
                              <SelectContent>
                                {otherDepts.map((dd) => (
                                  <SelectItem key={dd.id} value={dd.id}>{tData(dd)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onSelect={() => remove(m.id)}
                          className="text-destructive focus:text-destructive"
                          data-testid={`org-sheet-remove-${m.id}`}
                        >
                          <UserMinus className="h-3.5 w-3.5 mr-2" />
                          {t('pages.clientDepartments.org.removeFromDept', { defaultValue: 'Remove from department' })}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </li>
              ))}
            </ul>
          )}

          {canManageMembers && !assignOpen && (
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2 mt-4"
              onClick={openAssign}
              data-testid="org-sheet-assign-open"
            >
              <UserPlus className="h-3.5 w-3.5" />
              {t('pages.clientDepartments.org.assignExisting', { defaultValue: '+ Assign an existing employee' })}
            </Button>
          )}
          {canManageMembers && assignOpen && (
            <div className="mt-4 space-y-2 rounded-lg border p-3 bg-muted/20" data-testid="org-sheet-assign-panel">
              <p className="text-xs font-semibold text-muted-foreground">
                {t('pages.clientDepartments.org.assignPickerLabel', { defaultValue: 'Pick an employee with no department' })}
              </p>
              {candidates.length === 0 ? (
                <p className="text-xs italic text-muted-foreground">
                  {t('pages.clientDepartments.org.noCandidates', { defaultValue: 'Every employee is already assigned to a department.' })}
                </p>
              ) : (
                <Select value={assignId} onValueChange={setAssignId}>
                  <SelectTrigger data-testid="org-sheet-assign-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {candidates.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.position ? ` · ${c.position}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="ghost" onClick={() => { setAssignOpen(false); setAssignId(''); }}>
                  {t('pages.clientDepartments.cancel')}
                </Button>
                <Button
                  size="sm"
                  disabled={!assignId || assigning}
                  onClick={confirmAssign}
                  className="bg-gradient-primary"
                  data-testid="org-sheet-assign-save"
                >
                  {assigning ? t('common.saving') : t('common.confirm')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
